/**
 * useScreenQueryState — mapeia o estado de uma query do React Query para os 4
 * estados que uma tela precisa renderizar: loading | error | empty | data.
 *
 * Por que existe: derivar `loading` de TRANSIÇÕES de status (useEffect em
 * isSuccess/isError) quebra no caso `error → error` (refetch que falha de novo
 * não dispara o efeito → spinner eterno). Aqui o estado é derivado do estado
 * ATUAL da query, então é correto em qualquer sequência de transições.
 *
 * Considera `placeholderData: keepPrevious` (default global): durante um refetch
 * com erro, `data` pode conter o valor anterior — por isso as condições usam
 * `data === undefined` em vez de `!data` (evita falso-positivo com 0/'' /[]).
 */
export const useScreenQueryState = (query, { enabled = true } = {}) => {
    const hasNoData = query.data === undefined;

    const initialLoading = enabled && query.isPending;               // 1ª carga, sem dado
    const retrying       = query.isFetching && hasNoData;            // refetch sem dado em mãos
    const loading        = enabled ? (initialLoading || retrying) : false;

    // Erro só quando não há dado e não está buscando (senão é o spinner do retry).
    const error = query.isError && !query.isFetching && hasNoData ? query.error : null;

    const empty = query.isSuccess && (
        query.data == null ||
        (Array.isArray(query.data) && query.data.length === 0)
    );

    return {
        loading,
        error,
        empty,
        data: query.data,
        retry: query.refetch,
    };
};
